import { Button, Divider, Skeleton, Tooltip } from 'antd'
import { ErrorScreen } from 'components/ErrorScreen'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { useTicketStepContext } from './ticket/TicketStateContext'
import { useTicketUpdate } from './ticket/TicketUpdateContext'
import {
  LibraWorflowStepComponentProps,
  LibraWorkflowStepProps,
  StepValidationResult,
  UseValidateConfigHook,
} from './workflow/workflowStepsApi'

type StepModule = {
  Component: FC<LibraWorflowStepComponentProps<Record<string, unknown>>>
  useValidateConfig?: UseValidateConfigHook
}

type LoadedStepModule = {
  componentName: string
  module: StepModule
}

const useAlwaysValid: UseValidateConfigHook = () => true

export const WorkflowStepContainer: FC<{
  componentName: string
  config: Record<string, unknown>
}> = ({ componentName, config }) => {
  const [loadedStep, setLoadedStep] = useState<LoadedStepModule | null>(null)

  useEffect(() => {
    let isActive = true
    setLoadedStep(null)

    import(`./stepComponents/${componentName}.tsx`)
      .then((module) => {
        if (!isActive) {
          return
        }
        setLoadedStep({
          componentName,
          module: {
            Component: module.Component as StepModule['Component'],
            useValidateConfig: (module.useValidateConfig ??
              useAlwaysValid) as StepModule['useValidateConfig'],
          },
        })
      })
      .catch((error) => {
        console.error('Failed to load workflow step module', error)
      })

    return () => {
      isActive = false
    }
  }, [componentName])

  if (!loadedStep || loadedStep.componentName !== componentName) {
    return <Skeleton active paragraph={{ rows: 10 }} />
  }

  return (
    <LoadedWorkflowStep
      key={componentName}
      module={loadedStep.module}
      config={config}
      componentName={componentName}
    />
  )
}

const LoadedWorkflowStep: FC<{
  module: StepModule
  config: Record<string, unknown>
  componentName: string
}> = ({ module, config, componentName }) => {
  const { advanceTicketState } = useTicketUpdate()
  const { Component, useValidateConfig } = module
  const isValidConfig = (useValidateConfig ?? useAlwaysValid)({
    config,
    name: componentName,
  } satisfies LibraWorkflowStepProps<Record<string, unknown>>)

  const [validationResult, setValidationResult] = useState<
    StepValidationResult | undefined
  >(undefined)
  useEffect(() => {
    setValidationResult(undefined)
  }, [componentName, module])
  const { isActiveStep, hasMoreSteps, isWorkflowComplete } =
    useTicketStepContext()
  const handleAdvance = useCallback(() => {
    if (!isActiveStep) {
      return
    }
    void advanceTicketState()
  }, [advanceTicketState, isActiveStep])

  const tooltipTitle = useMemo(
    () =>
      getActionsTooltip(
        isActiveStep,
        hasMoreSteps,
        validationResult,
        isWorkflowComplete
      ),
    [isActiveStep, hasMoreSteps, validationResult, isWorkflowComplete]
  )

  return isValidConfig ? (
    <>
      <Component
        config={config}
        name={componentName}
        setValidationResult={setValidationResult}
      />
      <StyledDivider />
      <ActionsContainer>
        <Tooltip placement='topLeft' title={tooltipTitle}>
          <Button
            type='default'
            onClick={handleAdvance}
            disabled={Boolean(validationResult) || !isActiveStep}>
            {hasMoreSteps ? 'Next' : 'Complete'}
          </Button>
        </Tooltip>
      </ActionsContainer>
    </>
  ) : (
    <ErrorScreen
      title='Invalid Configuration'
      message='The configuration for this step failed validation.'
    />
  )
}

function getActionsTooltip(
  isActiveStep: boolean,
  hasMoreSteps: boolean,
  validationResult: StepValidationResult,
  isWorkflowComplete: boolean
): string {
  if (isWorkflowComplete) {
    return 'Workflow already complete'
  }
  if (!isActiveStep) {
    return 'This is not the active step'
  }
  if (validationResult) {
    return validationResult.message
  }
  if (!hasMoreSteps) {
    return 'Complete the workflow'
  }
  return 'Next step'
}

const StyledDivider = styled(Divider)`
  padding: 0.2rem;
`

const ActionsContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  padding-right: 1rem;
`
